import * as React from 'react';
import { DollarSign, Search, User, Lock } from 'lucide-react';
import { TextField } from '@components/base/inputs';
import { Icon, Text } from '@components/base/display';
import { VStack, HStack } from '@components/base/layout';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

const sizes = ['sm', 'md', 'lg'] as const;

function TextFieldShowcaseComponent({ playgroundProps, isDarkMode: _isDarkMode }: Readonly<ShowcaseProps>) {
  const [inputValue, setInputValue] = React.useState('');

  return (
    <VStack gap={6}>
      {/* Interactive Preview */}
      <ShowcasePreviewSection componentName="text field">
          <div className="max-w-md">
            <TextField
              label="Policy Number"
              placeholder="Enter policy number"
              size={playgroundProps.size as 'sm' | 'md' | 'lg'}
              disabled={playgroundProps.disabled as boolean}
              required={playgroundProps.required as boolean}
              error={playgroundProps.error as boolean}
              errorMessage={playgroundProps.error ? 'Invalid policy number format' : undefined}
              value={inputValue}
              onChange={(e) => { setInputValue(e.target.value); }}
            />
          </div>
      </ShowcasePreviewSection>

      {/* Size Variants */}
      <ShowcaseDemoSection
        title="Size Variants"
        description="Text fields come in three sizes: small (32px), medium (40px), and large (48px)"
       
      >
          <VStack gap={4} className="max-w-md">
            {sizes.map((size) => (
              <TextField
                key={size}
                label={`${size.toUpperCase()} Size`}
                placeholder={`${size} text field`}
                size={size}
              />
            ))}
          </VStack>
      </ShowcaseDemoSection>

      {/* With Labels */}
      <ShowcaseDemoSection
        title="With Labels"
        description="Text fields with descriptive labels above the input"
       
      >
          <VStack gap={4} className="max-w-md">
            <TextField
              label="Full Name"
              placeholder="John Doe"
            />
            <TextField
              label="Email Address"
              placeholder="john.doe@example.com"
              type="email"
            />
          </VStack>
      </ShowcaseDemoSection>

      {/* Required Fields */}
      <ShowcaseDemoSection
        title="Required Fields"
        description="Required fields display an asterisk indicator next to the label"
       
      >
          <VStack gap={4} className="max-w-md">
            <TextField
              label="Policyholder Name"
              placeholder="Enter name"
              required
            />
            <TextField
              label="Contact Email"
              placeholder="Enter email"
              type="email"
              required
            />
          </VStack>
      </ShowcaseDemoSection>

      {/* Helper Text */}
      <ShowcaseDemoSection
        title="Helper Text"
        description="Additional guidance displayed below the input"
       
      >
          <VStack gap={4} className="max-w-md">
            <TextField
              label="Claim Amount"
              placeholder="0.00"
              helperText="Enter the total claim amount in USD"
            />
            <TextField
              label="Policy Number"
              placeholder="POL-XXXX-XXXX"
              helperText="Found on the top-right corner of your policy document"
            />
          </VStack>
      </ShowcaseDemoSection>

      {/* Error States */}
      <ShowcaseDemoSection
        title="Error States"
        description="Text fields with validation errors"
       
      >
          <VStack gap={4} className="max-w-md">
            <TextField
              label="Email Address"
              placeholder="Enter email"
              value="invalid-email"
              error
              errorMessage="Please enter a valid email address"
            />
            <TextField
              label="Coverage Amount"
              placeholder="Enter amount"
              value="-500"
              error
              errorMessage="Coverage amount must be a positive number"
            />
          </VStack>
      </ShowcaseDemoSection>

      {/* Disabled State */}
      <ShowcaseDemoSection
        title="Disabled State"
        description="Disabled text fields cannot be edited"
       
      >
          <VStack gap={4} className="max-w-md">
            <TextField
              label="Account ID"
              value="ACC-2024-001234"
              disabled
            />
            <TextField
              label="Policy Type"
              value="Comprehensive Auto Insurance"
              disabled
              helperText="Contact support to change policy type"
            />
          </VStack>
      </ShowcaseDemoSection>

      {/* Prefix/Suffix */}
      <ShowcaseDemoSection
        title="Prefix and Suffix"
        description="Text fields with icons or text elements on either side"
       
      >
          <VStack gap={4} className="max-w-md">
            <TextField
              label="Premium Amount"
              placeholder="0.00"
              leftElement={<Icon icon={DollarSign} size="sm" />}
            />
            <TextField
              label="Search Policies"
              placeholder="Search by name or number"
              leftElement={<Icon icon={Search} size="sm" />}
            />
            <HStack gap={4} className="w-full">
              <TextField
                label="Username"
                placeholder="Enter username"
                leftElement={<Icon icon={User} size="sm" />}
              />
              <TextField
                label="Password"
                placeholder="Enter password"
                type="password"
                leftElement={<Icon icon={Lock} size="sm" />}
              />
            </HStack>
            <TextField
              label="Contact Email"
              placeholder="username"
              rightElement={<Text size="sm" color="muted">@company.com</Text>}
            />
          </VStack>
      </ShowcaseDemoSection>
    </VStack>
  );
}

export const TextFieldShowcase: ShowcaseDefinition = {
  id: 'text-field',
  name: 'TextField',
  category: 'inputs',
  description: 'Text input field with label, validation, and helper text support',
  propsDefinition: [
    {
      name: 'size',
      type: 'select',
      options: ['sm', 'md', 'lg'],
      defaultValue: 'md',
      description: 'Size of the text field (sm=32px, md=40px, lg=48px)',
    },
    {
      name: 'disabled',
      type: 'boolean',
      defaultValue: false,
      description: 'Whether the text field is disabled',
    },
    {
      name: 'required',
      type: 'boolean',
      defaultValue: false,
      description: 'Whether the field is required (shows asterisk)',
    },
    {
      name: 'error',
      type: 'boolean',
      defaultValue: false,
      description: 'Whether the field is in an error state',
    },
  ],
  accessibility: {
    ariaAttributes: [
      'aria-required (when required)',
      'aria-invalid (when error)',
      'aria-describedby (helper/error text)',
    ],
    keyboardNav: [
      'Tab: Move focus to/from field',
      'Enter: Submit form (in form context)',
    ],
    notes: [
      'Labels are linked via htmlFor/id',
      'Error messages announced to screen readers',
      'Focus state provides visible 2px ring',
    ],
  },
  component: TextFieldShowcaseComponent,
};
